import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAudio } from './AudioContext';
import { BettyTrack as Track } from '../types/music';
import { BETTY_API_URL } from '../config/api';
import { logError } from '../utils/logger';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  tracks?: Track[];
  isLoading?: boolean;
}

interface ParsedQuery {
  project_type?: string;
  moods: string[];
  genres: string[];
  year_start?: number;
  year_end?: number;
  themes: string[];
  instrumental_only: boolean;
  search_query: string;
}

interface BettyContextType {
  messages: ChatMessage[];
  isLoading: boolean;
  sendMessage: (message: string) => Promise<void>;
  clearHistory: () => void;
  playTrack: (track: Track) => void;
  searchSimilar: (trackId: string) => Promise<void>;
  lastParsedQuery: ParsedQuery | null;
  // Search sidebar state
  searchResults: Track[];
  totalResults: number;
  lastQuery: string;
  isSidebarOpen: boolean;
  openSidebar: (tracks?: Track[], query?: string) => void;
  closeSidebar: () => void;
  // Voice input state
}

const BettyContext = createContext<BettyContextType | undefined>(undefined);

const STORAGE_KEY = 'betty_chat_history';
const MAX_HISTORY_MESSAGES = 50;

// Grouped sidebar state to reduce rerenders
interface SidebarState {
  searchResults: Track[];
  totalResults: number;
  lastQuery: string;
  isOpen: boolean;
}

const initialSidebarState: SidebarState = {
  searchResults: [],
  totalResults: 0,
  lastQuery: '',
  isOpen: false,
};

export const BettyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastParsedQuery, setLastParsedQuery] = useState<ParsedQuery | null>(null);

  // Ref to access current messages without causing callback recreation
  const messagesRef = useRef<ChatMessage[]>(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Search sidebar state - grouped to reduce rerenders
  const [sidebarState, setSidebarState] = useState<SidebarState>(initialSidebarState);

  const { playTrack: audioPlayTrack } = useAudio();

  // Define openSidebar first to avoid circular dependency
  const openSidebar = useCallback((tracks?: Track[], query?: string) => {
    setSidebarState(prev => ({
      searchResults: tracks ?? prev.searchResults,
      totalResults: tracks ? tracks.length : prev.totalResults,
      lastQuery: query ?? prev.lastQuery,
      isOpen: true,
    }));
  }, []);

  // Load chat history from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Convert timestamp strings back to Date objects
        const messagesWithDates = parsed.map((msg: unknown) => ({
          ...(msg as ChatMessage),
          timestamp: new Date((msg as Record<string, string>).timestamp)
        }));
        setMessages(messagesWithDates);
      }
    } catch (error) {
      logError(error, { component: 'BettyContext', action: 'loadChatHistory' });
    }
  }, []);

  // Save chat history to localStorage whenever it changes
  useEffect(() => {
    if (messages.length > 0) {
      try {
        // Keep only the most recent messages
        const toSave = messages.slice(-MAX_HISTORY_MESSAGES);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
      } catch (error) {
        logError(error, { component: 'BettyContext', action: 'saveChatHistory' });
      }
    }
  }, [messages]);

  const sendMessage = useCallback(async (message: string) => {
    // Create messages upfront
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date()
    };

    const loadingMessage: ChatMessage = {
      id: `loading-${Date.now()}`,
      role: 'assistant',
      content: 'Betty is thinking...',
      timestamp: new Date(),
      isLoading: true
    };

    // Batch both messages in a single state update to avoid intermediate rerenders
    setMessages(prev => [...prev, userMessage, loadingMessage]);
    setIsLoading(true);

    try {
      // Prepare conversation history for API (use ref to avoid dependency on messages)
      const conversationHistory = messagesRef.current
        .filter(msg => !msg.isLoading)
        .slice(-10) // Last 10 messages for context
        .map(msg => ({
          role: msg.role,
          content: msg.content
        }));

      // Send to Betty API
      const response = await axios.post(`${BETTY_API_URL}/chat`, {
        query: message,
        conversation_history: conversationHistory,
        filters: {}
      });

      // Support multiple backend response shapes
      const data = response.data || {};
      const bettyResponse: string = (data.message ?? data.response ?? '').toString();
      const tracks: Track[] = (data.tracks ?? data.formatted_items ?? []) as Track[];
      const parsed_query = data.parsed_query ?? null;
      const total_results = data.total_results ?? (Array.isArray(tracks) ? tracks.length : 0);

      // Update parsed query
      if (parsed_query) {
        setLastParsedQuery(parsed_query);
      }

      // Update search results for sidebar (single state update)
      setSidebarState(prev => ({
        ...prev,
        searchResults: tracks || [],
        totalResults: total_results || 0,
        lastQuery: message,
      }));

      // Replace loading message with actual response
      setMessages(prev => {
        const filtered = prev.filter(msg => msg.id !== loadingMessage.id);
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: bettyResponse,
          timestamp: new Date(),
          tracks: tracks || []
        };


        return [...filtered, assistantMessage];
      });

      // Auto-play and open sidebar if tracks available
      if (tracks && tracks.length > 0) {
        audioPlayTrack({
          id: tracks[0].id,
          title: tracks[0].title,
          artist: tracks[0].artist,
          url: tracks[0].url,
          coverArt: tracks[0].coverArt || tracks[0].cover_art || undefined,
          album: tracks[0].album_title
        });
        openSidebar(tracks, message);
      }
    } catch (error) {
      logError(error, { component: 'BettyContext', action: 'sendMessage' });

      // Replace loading message with error
      setMessages(prev => {
        const filtered = prev.filter(msg => msg.id !== loadingMessage.id);
        const errorMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: "I'm having trouble connecting right now. Please try again in a moment.",
          timestamp: new Date()
        };
        return [...filtered, errorMessage];
      });
    } finally {
      setIsLoading(false);
    }
  }, [openSidebar, audioPlayTrack]);

  const playTrack = useCallback((track: Track) => {
    // Convert Betty track format to audio player format
    audioPlayTrack({
      id: track.id,
      title: track.title,
      artist: track.artist,
      url: track.url,
      coverArt: track.coverArt || track.cover_art || undefined,
      album: track.album_title
    });
  }, [audioPlayTrack]);

  const searchSimilar = useCallback(async (trackId: string) => {
    try {
      const response = await axios.get(`${BETTY_API_URL}/search/similar/${trackId}`);
      const { tracks } = response.data;

      // Add a message about similar tracks
      const message: ChatMessage = {
        id: `similar-${Date.now()}`,
        role: 'assistant',
        content: `I found ${tracks.length} tracks similar to that one. Here they are:`,
        timestamp: new Date(),
        tracks
      };
      setMessages(prev => [...prev, message]);

      // Play the first similar track and open sidebar
      if (tracks.length > 0) {
        playTrack(tracks[0]);
        openSidebar(tracks, `Similar to: ${trackId}`);
      }
    } catch (error) {
      logError(error, { component: 'BettyContext', action: 'searchSimilar' });
    }
  }, [playTrack, openSidebar]);

  const clearHistory = useCallback(() => {
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY);
    setLastParsedQuery(null);
    setSidebarState(initialSidebarState);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarState(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Memoize context value to prevent unnecessary rerenders
  const value: BettyContextType = useMemo(() => ({
    messages,
    isLoading,
    sendMessage,
    clearHistory,
    playTrack,
    searchSimilar,
    lastParsedQuery,
    searchResults: sidebarState.searchResults,
    totalResults: sidebarState.totalResults,
    lastQuery: sidebarState.lastQuery,
    isSidebarOpen: sidebarState.isOpen,
    openSidebar,
    closeSidebar,
  }), [
    messages,
    isLoading,
    sendMessage,
    clearHistory,
    playTrack,
    searchSimilar,
    lastParsedQuery,
    sidebarState,
    openSidebar,
    closeSidebar,
  ]);

  return <BettyContext.Provider value={value}>{children}</BettyContext.Provider>;
};

export const useBetty = () => {
  const context = useContext(BettyContext);
  if (!context) {
    throw new Error('useBetty must be used within a BettyProvider');
  }
  return context;
};